import { VC_BOARD_TYPES } from '../../../../../../constants/general';
import {
  BUSINESS_UNIT_TYPES,
  VALUE_CHAIN_MAP_CUSTOM_NODES,
  VALUE_CHAIN_PANEL_NODES_TYPES,
  VALUE_CHAIN_UNIT_TYPES,
} from '../../../../../../constants/interfaces';

import type { Node } from '@xyflow/react';
import type { TValueChainPanelNodesType } from '../../../../../../constants/interfaces';

/** i18n keys for notifications (use with t('notifications.<key>')) */
export const VALUE_CHAIN_PLACEMENT_ERROR_KEYS = {
  SUPPLIER_OUTSIDE_UPSTREAM: 'valueChainPlacementSupplierOutsideUpstream',
  PRODUCT_INPUT_OUTSIDE_UPSTREAM: 'valueChainPlacementProductInputOutsideUpstream',
  OUTPUT_PRODUCT_OUTSIDE_OWN_OPERATIONS: 'valueChainPlacementOutputProductOutsideOwnOperations',
  CUSTOMER_OUTSIDE_DOWNSTREAM: 'valueChainPlacementCustomerOutsideDownstream',
  OWN_OPERATIONS_OUTSIDE_SECTION: 'valueChainPlacementOwnOperationsOutsideSection',
  OUTSIDE_SECTION: 'valueChainPlacementOutsideSection',
} as const;

const PANEL_TYPE_TO_SECTION: Partial<Record<TValueChainPanelNodesType | string, string>> = {
  [VALUE_CHAIN_PANEL_NODES_TYPES.SUPPLIER]: VC_BOARD_TYPES.UPSTREAM,
  [VALUE_CHAIN_PANEL_NODES_TYPES.INPUT_PRODUCT]: VC_BOARD_TYPES.UPSTREAM,
  [VALUE_CHAIN_PANEL_NODES_TYPES.CUSTOMER]: VC_BOARD_TYPES.DOWNSTREAM,
  [VALUE_CHAIN_PANEL_NODES_TYPES.PROFIT_CENTER]: VC_BOARD_TYPES.OWN_OPERATIONS,
  [VALUE_CHAIN_PANEL_NODES_TYPES.BALANCE_SHEET_ASSET]: VC_BOARD_TYPES.OWN_OPERATIONS,
};

const UNIT_TYPE_TO_SECTION: Partial<Record<string, string>> = {
  [VALUE_CHAIN_UNIT_TYPES.SUPPLIER]: VC_BOARD_TYPES.UPSTREAM,
  [VALUE_CHAIN_UNIT_TYPES.PRODUCT]: VC_BOARD_TYPES.UPSTREAM,
  [VALUE_CHAIN_UNIT_TYPES.OUTPUT_PRODUCT]: VC_BOARD_TYPES.OWN_OPERATIONS,
  [VALUE_CHAIN_UNIT_TYPES.CUSTOMER]: VC_BOARD_TYPES.DOWNSTREAM,
  [VALUE_CHAIN_UNIT_TYPES.PROFIT_CENTER]: VC_BOARD_TYPES.OWN_OPERATIONS,
  [VALUE_CHAIN_UNIT_TYPES.BALANCE_SHEET_ASSET]: VC_BOARD_TYPES.OWN_OPERATIONS,
};

export const getSectionForPanelType = (
  panelType: TValueChainPanelNodesType | string,
): string | undefined => PANEL_TYPE_TO_SECTION[panelType];

export const getSectionForUnitType = (
  unitType: string,
): string | undefined => UNIT_TYPE_TO_SECTION[unitType];

export const isSectionValidForPanelType = (
  boardName: string,
  panelType: TValueChainPanelNodesType | string,
): boolean => {
  if (panelType === VALUE_CHAIN_PANEL_NODES_TYPES.INPUT_PRODUCT) {
    return boardName === VC_BOARD_TYPES.UPSTREAM || boardName === VC_BOARD_TYPES.OWN_OPERATIONS;
  }

  const requiredSection = getSectionForPanelType(panelType);
  return requiredSection !== undefined && boardName === requiredSection;
};

export const isSectionValidForUnitType = (boardName: string, unitType: string): boolean => {
  if (unitType === BUSINESS_UNIT_TYPES.LOGICAL) {
    return true;
  }

  if (unitType === VALUE_CHAIN_UNIT_TYPES.PRODUCT) {
    return boardName === VC_BOARD_TYPES.UPSTREAM;
  }

  if (unitType === VALUE_CHAIN_UNIT_TYPES.OUTPUT_PRODUCT) {
    return boardName === VC_BOARD_TYPES.OWN_OPERATIONS;
  }

  const requiredSection = getSectionForUnitType(unitType);
  return requiredSection !== undefined && boardName === requiredSection;
};

export const getBoardBySection = (nodes: Node[], sectionName: string): Node | undefined => nodes.find(
  (n) => n.type === VALUE_CHAIN_MAP_CUSTOM_NODES.BOARD && n.data?.name === sectionName,
);

export const getBoardForSwimlane = (nodes: Node[], swimlaneNode: Node): Node | undefined => {
  const boardId = swimlaneNode.data?.boardId;
  if (boardId == null) return undefined;
  return nodes.find(
    (n) => n.type === VALUE_CHAIN_MAP_CUSTOM_NODES.BOARD && Number(n.data?.id) === Number(boardId),
  );
};

export const getPlacementErrorMessageKey = (
  panelType: TValueChainPanelNodesType | string,
): string => {
  switch (panelType) {
    case VALUE_CHAIN_PANEL_NODES_TYPES.SUPPLIER:
      return VALUE_CHAIN_PLACEMENT_ERROR_KEYS.SUPPLIER_OUTSIDE_UPSTREAM;
    case VALUE_CHAIN_PANEL_NODES_TYPES.INPUT_PRODUCT:
      return VALUE_CHAIN_PLACEMENT_ERROR_KEYS.PRODUCT_INPUT_OUTSIDE_UPSTREAM;
    case VALUE_CHAIN_PANEL_NODES_TYPES.CUSTOMER:
      return VALUE_CHAIN_PLACEMENT_ERROR_KEYS.CUSTOMER_OUTSIDE_DOWNSTREAM;
    case VALUE_CHAIN_PANEL_NODES_TYPES.PROFIT_CENTER:
    case VALUE_CHAIN_PANEL_NODES_TYPES.BALANCE_SHEET_ASSET:
      return VALUE_CHAIN_PLACEMENT_ERROR_KEYS.OWN_OPERATIONS_OUTSIDE_SECTION;
    default:
      return VALUE_CHAIN_PLACEMENT_ERROR_KEYS.OUTSIDE_SECTION;
  }
};

export const getPlacementErrorMessageKeyForUnitType = (unitType: string): string => {
  switch (unitType) {
    case VALUE_CHAIN_UNIT_TYPES.SUPPLIER:
      return VALUE_CHAIN_PLACEMENT_ERROR_KEYS.SUPPLIER_OUTSIDE_UPSTREAM;
    case VALUE_CHAIN_UNIT_TYPES.PRODUCT:
      return VALUE_CHAIN_PLACEMENT_ERROR_KEYS.PRODUCT_INPUT_OUTSIDE_UPSTREAM;
    case VALUE_CHAIN_UNIT_TYPES.OUTPUT_PRODUCT:
      return VALUE_CHAIN_PLACEMENT_ERROR_KEYS.OUTPUT_PRODUCT_OUTSIDE_OWN_OPERATIONS;
    case VALUE_CHAIN_UNIT_TYPES.CUSTOMER:
      return VALUE_CHAIN_PLACEMENT_ERROR_KEYS.CUSTOMER_OUTSIDE_DOWNSTREAM;
    case VALUE_CHAIN_UNIT_TYPES.PROFIT_CENTER:
    case VALUE_CHAIN_UNIT_TYPES.BALANCE_SHEET_ASSET:
      return VALUE_CHAIN_PLACEMENT_ERROR_KEYS.OWN_OPERATIONS_OUTSIDE_SECTION;
    default:
      return VALUE_CHAIN_PLACEMENT_ERROR_KEYS.OUTSIDE_SECTION;
  }
};
